import { BlurbBlocks } from 'components/city-blocks/blurb-blocks';
import { HeaderCopy } from 'components/city-blocks/header-copy';
import { HeroSection } from 'components/city-blocks/hero-section';
import { LinkColumns } from 'components/city-blocks/link-columns';
import { MaxWidthContainer } from 'components/city-blocks/max-width-container';
import { PopularFoodLocations } from 'components/city-blocks/popular-food-locations';
import { WavyDivider } from 'components/flair/WavyDivider';
import { HeroBlock } from 'components/hero-block/hero-block';
import Layout from 'components/layout';
import {
  generateBreadcrumbSchema,
  generateLocalBusinessSchema,
  generateOrganizationSchema,
  generateServiceSchema,
  generateWebSiteSchema,
  SchemaMarkup,
  SEOHead,
} from 'components/seo';
import React from 'react';
import {
  AUTHENTIC_BLURBS,
  HEADER_COPY_CONTENT_2,
  HEADER_COPY_TITLE_2,
  HEADER_COPY_TITLE_3,
  HEADER_COPY_TITLE_4,
  META_CONTENT,
  STORY_BLURBS,
} from 'shared/indexContent';
import { fetchHomepageDataOnly } from 'utils/api-utils';
import { rootBreadcrumbs } from 'utils/breadcrumb-util';
import { BASE_PATH } from 'utils/constants';
import { getFrontEndUrl } from 'utils/environment-util';
import { RegionInfoFragment } from 'utils/gqlReactTypings.generated.d';
import { memoizedCityPageCities, memoizedRegions } from 'utils/memoized-data-structure';
import { reduceShallowCityPage, reduceZipPage, ShallowCityPage, ZipPage } from 'utils/types';

export const LINKS_BLOCK_LIMIT = 5;

interface IProps {
  regions: RegionInfoFragment[];
  cityPages: ShallowCityPage[];
  zipPages: ZipPage[];
}

const Home: React.FC<IProps> = ({ regions, cityPages, zipPages }) => {
  const siteUrl = getFrontEndUrl();
  const pageUrl = `${siteUrl}${BASE_PATH}`;

  const schemas = [
    generateOrganizationSchema({
      name: 'Shef',
      url: siteUrl,
      logo: `${siteUrl}/images/shef-logo.svg`,
      contactPoint: {
        contactType: 'Customer Service',
        areaServed: 'US',
      },
    }),
    generateWebSiteSchema(siteUrl, `${siteUrl}/explore`),
    generateServiceSchema(),
    generateLocalBusinessSchema({
      name: 'Shef',
      description: 'Homemade food delivery service',
      url: siteUrl,
      areaServed: regions.map((r) => r.displayName),
      priceRange: '$$',
    }),
    generateBreadcrumbSchema([
      { name: 'Home', url: siteUrl },
      { name: 'Food Delivery', url: `${siteUrl}/homemade-food-delivery` },
    ]),
  ];

  return (
    <Layout>
      <SEOHead
        title='Homemade Food Delivery - Order Authentic Local Meals'
        description={META_CONTENT}
        url={pageUrl}
        keywords={[
          'homemade food delivery',
          'local chefs',
          'authentic meals',
          'home cooked food',
          'food delivery near me',
          'local food delivery',
          'chef-prepared meals',
          'authentic cuisine delivery',
          'home-style cooking delivery',
        ]}
        image={`${siteUrl}${BASE_PATH}/images/CDN/family_table.png`}
        type='website'
        canonical={pageUrl}
      />
      <SchemaMarkup schemas={schemas} id='homepage-schema' />
      <HeroBlock className='mb-6 md:mb-10' heroText='Where Does Shef Deliver?' crumbs={rootBreadcrumbs} />
      <MaxWidthContainer className='px-4 md:px-0'>
        <LinkColumns regions={regions} cityPages={cityPages} zipPages={zipPages} />
        <PopularFoodLocations />
        <HeroSection />
        <WavyDivider className='pb-8 md:pb-16' />
        <HeaderCopy className='mb-8 md:mb-16' title={HEADER_COPY_TITLE_2} content={HEADER_COPY_CONTENT_2} />
        <WavyDivider className='pb-8 md:pb-16' />
        <HeaderCopy className='mb-8 md:mb-16' title={HEADER_COPY_TITLE_3} />
        <BlurbBlocks blurbList={AUTHENTIC_BLURBS} />
        <WavyDivider className='pb-8 md:pb-16' />
        <HeaderCopy className='mb-8 md:mb-16' title={HEADER_COPY_TITLE_4} />
        <BlurbBlocks blurbList={STORY_BLURBS} />
      </MaxWidthContainer>
    </Layout>
  );
};

export async function getStaticProps(): Promise<{ props: IProps }> {
  // Allow local builds without API by skipping SSG data fetches entirely
  if (process.env.SKIP_SSG_FETCH === 'true' || process.env.NEXT_PUBLIC_SKIP_SSG === 'true') {
    return {
      props: {
        regions: [],
        cityPages: [],
        zipPages: [],
      },
    };
  }

  const homepageData = await fetchHomepageDataOnly();

  // Prefer pre-rendered production JSON to avoid GraphQL calls when possible
  if (homepageData) {
    return {
      props: {
        regions: homepageData.regions?.slice(0, LINKS_BLOCK_LIMIT) ?? [],
        cityPages: homepageData.cityPages?.slice(0, LINKS_BLOCK_LIMIT) ?? [],
        zipPages: homepageData.zipPages?.slice(0, LINKS_BLOCK_LIMIT) ?? [],
      },
    };
  }

  // Fallback: compute from GraphQL-backed memoized structures only if needed
  const regions = (await memoizedRegions()) ?? [];
  const cityPageCities = await memoizedCityPageCities();

  return {
    props: {
      regions: regions.slice(0, LINKS_BLOCK_LIMIT),
      cityPages: cityPageCities.map(reduceShallowCityPage).slice(0, LINKS_BLOCK_LIMIT),
      zipPages: cityPageCities.map((cityPage) => reduceZipPage(cityPage, cityPage.zipCode)).slice(0, LINKS_BLOCK_LIMIT),
    },
  };
}

export default Home;
